import React, { useState, useEffect, useRef, useContext } from 'react';
import { Row, Col, Label, Input, FormGroup } from 'reactstrap';
import classNames from 'classnames';
import styles from './Login.module.css';
import SimpleReactValidator from 'simple-react-validator';
import Cookies from 'js-cookie';
import { SessionContext } from '../context/SessionContext';
import EnrollmentModal from '../components/Modals/Enrollment/Enrollment';
import { useHistory, Redirect } from 'react-router-dom';
import { AuthErrorCodes } from '../components/Global/AuthConstants';
import { getBrandContent, getProfileMenus } from '../components/BrandingWhiteLabel/BrandContent';
import { stripPhoneNumber } from '../lib/util';

const LoginForm = (props) => {
    const [formData, setFormData] = useState({
        dealerid: '',
        password: '',
        rememberMe: false
    });
    const [validationCheck, setValidationCheck] = useState(false);
    const [loginAttemptFailed, setLoginAttemptFailed] = useState(false);
    const [, forceUpdate] = useState();
    const [loginFailedCount, setLoginFailedCount] = useState(1);
    const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
    const [disableButton, setDisableButton] = useState(false);
    const [accountInactive, setAccountInactive] = useState(false);
    const [loginError, setLoginError] = useState(false);
    const history = useHistory();

    const simpleValidator = useRef(
        new SimpleReactValidator({
            element: (message) => (
                <div className={getErrorMsgStyles(accountNameToLower)}>
                    {message.charAt(0).toUpperCase() + message.slice(1)}
                </div>
            ),
            messages: {
                required: ':attribute is required.',
                email: ':attribute is invalid.'
            }
        })
    );

    var sessionContext = useContext(SessionContext);

    useEffect(() => {
        updateRememberMeFields();
        (sessionContext.errorCode === AuthErrorCodes.dealerNotFound
            || sessionContext.errorCode === AuthErrorCodes.inactiveDealer)
            && setAccountInactive(true);
        sessionContext.errorCode === AuthErrorCodes.systemError && setLoginError(true);
    }, []);

    const handleRedirect = (e) => {
        e.preventDefault();
        sessionContext.updateForgotPassLink(true);
    };

    const handleInputChange = (e) => {
        let { name, value } = e.target;

        if (name === 'rememberMe') {
            setFormData(
                {
                    ...formData,
                    rememberMe: !formData.rememberMe
                },
                updateRememberMeCookies(!formData.rememberMe)
            );
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();

        setDisableButton(true);

        if (simpleValidator.current.allValid()) {
            setValidationCheck(false);
            var ret = await authenticateUser();

            setAccountInactive(false);
            setLoginError(false);
            if (ret == false) {
                setLoginAttemptFailed(true);
                setLoginFailedCount(loginFailedCount + 1);

                if (loginFailedCount > 3) {
                    sessionContext.setLoginExceeded(true);
                    history.push('/');
                }
            } else {
                // Implement logic to pull the emails returned from authenticateUser to send a verification code to.
                // This existing logic will need to be moved to when the token is successfully validated.
                setLoginAttemptFailed(false);
                updateRememberMeFields();
                sessionContext.setIsResetPasswordSuccess(false);
            }
        } else {
            setValidationCheck(true);
            simpleValidator.current.showMessages();
            forceUpdate(1);
        }

        setDisableButton(false);
    };

    const authenticateUser = async () => {
        var model = {
            userEmail: formData.dealerid, // TODO: Remove when switching to user email.
            password: formData.password,
            rememberMe: formData.rememberMe
            // UserEmail: formData.dealerid // TODO: Change to user email
        };

        var response = await fetch('authentication/authenticateuser/', {
            method: 'post',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(model)
        });

        if (response.ok) {
            const result = await response.json();
            if (result.succeeded) {
                // TODO: Direct to page with emails to select for the verification code
                setLoginAttemptFailed(false);
                updateRememberMeFields();
                sessionContext.setLoginByEmailStatus(true);
                sessionContext.setIsForgotPassword(false);
                history.push({
                    pathname: '/verifyemail',
                    state: {
                        emails: result.emails,
                        dealerInfo: result
                    }
                });
                return true;
            }
        }
        return false;
    };

    const updateRememberMeFields = () => {
        var dealerId = Cookies.get('dealerid');
        var rememberMe = Cookies.get('rememberMe');
        if (rememberMe && dealerId) {
            setFormData({
                ...formData,
                dealerid: dealerId,
                rememberMe: rememberMe === 'true'
            });
        }
    };

    const updateRememberMeCookies = (rememberMe) => {
        if (rememberMe) {
            Cookies.set('dealerid', formData.dealerid, { expires: 365 });
            Cookies.set('rememberMe', true, { expires: 365 });
        } else {
            Cookies.remove('dealerid');
            Cookies.remove('rememberMe');
        }
    };

    const handleShowActionModal = () => {
        setShowEnrollmentModal(true);
    };

    const getCheckboxStyles = (account) => {
        return `checkboxLabel ${sessionContext.accountName.replace(/\s+/g, '')} 
            ${account === 'icp' && 'invertedCheckboxICP'}
            ${account === 'homedepot' && 'invertedCheckboxHD'}
            ${account === 'mcc' && 'invertedCheckboxMCC'}
            ${account === 'nordyne' && 'invertedCheckboxNordyne'}`;
    };

    const getButtonStyles = (account) => {
        if (account === 'ge') {
            return styles.loginBtnGE;
        }
        if (account === 'icp') {
            return styles.loginBtnICP;
        }
        if (account === 'homedepot') {
            return styles.loginBtnHD;
        }
        if (account === 'mcc') {
            return styles.loginBtnMCC;
        }
        if (account === 'trane') {
            return styles.loginBtnTrane;
        }
        if (account === 'nordyne') {
            return styles.loginBtnNordyne;
        }
        if (account === 'mainline') {
            return styles.loginBtnMainline;
        }
        return 'btn-primary';
    };

    const getErrorMsgStyles = (account) => {
        switch (account) {
            case 'icp':
                return `${styles.errorMsgICP} errorMsg`;
            case 'mainline':
                return `${styles.errorMainline} errorMsg`;
            default:
                return 'errorMsg';
        }
    };

    const getValidationMsgStyles = (account) => {
        switch (account) {
            case 'icp':
                return `${styles.errorMsgICP} validationSummary mb-3`;
            case 'mainline':
                return `${styles.errorMainline} validationSummary mb-3`;
            default:
                return 'validationSummary mb-3';
        }
    };

    const { accountName } = sessionContext;
    const accountNameToLower = accountName.replace(/\s+/g, '').toLowerCase();

    if (sessionContext.forgotPassClick) {
        return <Redirect to='/forgotpassword' />;
    }

    return (
        <>
            <h1 className={classNames('h3', accountNameToLower === 'homedepot' && styles.loginHeadingHD)}>Welcome</h1>
            <FormGroup
                className={
                    !simpleValidator.current.check(formData.dealerid, 'required|email') && validationCheck
                        ? 'form-error'
                        : ''
                }
            >
                <label
                    for='dealerid'
                    id='dealerid'
                    className={classNames('form-label', accountNameToLower === 'trane' ? styles.traneLabelText : '')}
                >
                    Company Email
                </label>
                <span
                    aria-hidden='true'
                    className={classNames(
                        'required-indicator',
                        accountNameToLower === 'homedepot' && styles.requiredIndicatorHD,
                        accountNameToLower === 'mcc' && styles.requiredIndicatorMCC,
                        accountNameToLower === 'icp' && styles.requiredIndicatorICP,
                        accountNameToLower === 'mainline' && styles.requiredIndicatorMainline
                    )}
                >
                    {' '}
                    *
                </span>
                <Input
                    name='dealerid'
                    aria-labelledby='dealerid'
                    type='email'
                    className={classNames(
                        'form-control',
                        (accountNameToLower === 'ge'
                            || accountNameToLower === 'icp'
                            || accountNameToLower === 'homedepot'
                            || accountNameToLower === 'trane')
                            && styles.inputStyles
                    )}
                    defaultValue={formData.dealerid}
                    onChange={handleInputChange}
                />
                {simpleValidator.current.message('dealerId', formData.dealerid, 'required|email', {
                    messages: {
                        required: 'Company Email is required.',
                        email: 'Company Email is invalid.'
                    }
                })}
            </FormGroup>
            <FormGroup
                className={
                    !simpleValidator.current.check(formData.password, 'required') && validationCheck
                        ? 'form-error mb-0'
                        : 'mb-0'
                }
            >
                <label
                    for='password'
                    id='password'
                    className={classNames('form-label', accountNameToLower === 'trane' ? styles.traneLabelText : '')}
                >
                    Password
                </label>
                <span
                    aria-hidden='true'
                    className={classNames(
                        'required-indicator',
                        accountNameToLower === 'homedepot' && styles.requiredIndicatorHD,
                        accountNameToLower === 'mcc' && styles.requiredIndicatorMCC,
                        accountNameToLower === 'icp' && styles.requiredIndicatorICP,
                        accountNameToLower === 'mainline' && styles.requiredIndicatorMainline
                    )}
                >
                    {' '}
                    *
                </span>
                <Input
                    name='password'
                    aria-labelledby='password'
                    type='password'
                    className={classNames(
                        'form-control',
                        (accountNameToLower === 'ge'
                            || accountNameToLower === 'icp'
                            || accountNameToLower === 'homedepot'
                            || accountNameToLower === 'trane')
                            && styles.inputStyles
                    )}
                    defaultValue={formData.password}
                    onChange={handleInputChange}
                />
                {simpleValidator.current.message('password', formData.password, 'required')}
            </FormGroup>
            <Row>
                <div className={classNames('checkbox mt-2 col-6', accountNameToLower === 'icp' && styles.checkboxICP)}>
                    <Input
                        className='mr-2'
                        type='checkbox'
                        name='rememberMe'
                        checked={formData.rememberMe}
                        autocomplete='current-password'
                        onChange={handleInputChange}
                        id='rememberMe'
                    />
                    <Label
                        for='rememberMe'
                        className={getCheckboxStyles(accountNameToLower)}
                    >
                        Remember Me
                    </Label>
                </div>
                <div className={classNames('mt-2 col-6', styles.alignRight)}>
                    <a
                        href=''
                        className={styles.forgotPassLink}
                        onClick={handleRedirect}
                    >
                        Forgot Password?
                    </a>
                </div>
            </Row>
            <div className={styles.clear}></div>
            {accountInactive && accountNameToLower !== 'ge' && (
                <div className={`${styles.summary} validationSummary`}>
                    We were unable to log you in with single sign-on.&nbsp;
                    <a onClick={handleShowActionModal}>Enroll now</a> to join {getBrandContent(accountName).programName}{' '}
                    or contact support at{' '}
                    <a
                        href={`tel:${stripPhoneNumber(getBrandContent(accountName).programPhone)}`}
                        aria-label='Click to Call'
                        className={`${styles.visibleOne} callLink`}
                    >
                        {getBrandContent(accountName).programPhone}
                    </a>
                    .
                </div>
            )}
            {loginError && (
                <div className={getValidationMsgStyles(accountNameToLower)}>
                    We were unable to log you in with single sign-on. Please login below or{' '}
                    <a href='/forgotpassword'> create a password</a> for direct login access.
                </div>
            )}
            {loginAttemptFailed && (
                <div className={getValidationMsgStyles(accountNameToLower)}>
                    The login you have entered does not match our records.
                </div>
            )}
            <div className={styles.loginBtnWrapper}>
                <button
                    className={classNames(getButtonStyles(accountNameToLower))}
                    onClick={handleLogin}
                    disabled={disableButton}
                >
                    Login
                </button>
            </div>
            {getProfileMenus(accountName)?.showEnrollModal && (
                <Col
                    md='12'
                    className={styles.enrollNow}
                >
                    Don't have an account?
                    {showEnrollmentModal && (
                        <EnrollmentModal
                            isOpen={showEnrollmentModal}
                            centered={true}
                            toggle={() => setShowEnrollmentModal(!showEnrollmentModal)}
                            accountName={accountName}
                        />
                    )}
                    <a
                        onClick={(e) => handleShowActionModal()}
                        className={classNames('btn-link', styles.enrollmentLink)}
                        title='Click to open a modal to enroll'
                    >
                        <b>Enroll Now</b>
                    </a>
                </Col>
            )}
        </>
    );
};

export default LoginForm;
